/**
 * credits.ts — Firestore helpers for managing debate credits.
 *
 * Data model:
 *   users/{uid}/profile/credits  → { plusCredits, proCredits, initialized }
 *   users/{uid}/profile/usage    → { weekStart, freeDebates, audioDebates }
 *
 * Every new user starts with 1 free Plus debate and no Pro debates.
 * Balances are changed only by Cloud Functions (startDebate, the Stripe
 * webhook); Firestore rules let the client create the default doc and read it.
 * Weekly limits reset every Monday at 00:00 UTC.
 */

import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "./config";

export interface DebateCredits {
  plusCredits: number;
  proCredits: number;
  chatCredits: number;
  initialized: boolean;
}

const DEFAULT_CREDITS: DebateCredits = {
  plusCredits: 1,
  proCredits: 0,
  chatCredits: 0,
  initialized: true,
};

function creditsRef(uid: string) {
  return doc(db, "users", uid, "profile", "credits");
}

/** Ensures the credits doc exists (creates with defaults if missing). Returns current credits. */
export async function getOrInitCredits(uid: string): Promise<DebateCredits> {
  const ref = creditsRef(uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data() as any;
    return {
      plusCredits: data.plusCredits ?? 0,
      proCredits: data.proCredits ?? 0,
      chatCredits: data.chatCredits ?? 0,
      initialized: data.initialized ?? false,
    };
  }
  // First time — seed with free credits
  await setDoc(ref, DEFAULT_CREDITS);
  return { ...DEFAULT_CREDITS };
}

/** Subscribe to real-time credit updates. */
export function onCreditsSnapshot(
  uid: string,
  callback: (credits: DebateCredits) => void,
): Unsubscribe {
  const ref = creditsRef(uid);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      const data = snap.data() as any;
      callback({
        plusCredits: data.plusCredits ?? 0,
        proCredits: data.proCredits ?? 0,
        chatCredits: data.chatCredits ?? 0,
        initialized: data.initialized ?? false,
      });
    } else {
      // Auto-init if it doesn't exist yet
      setDoc(ref, DEFAULT_CREDITS).then(() => callback({ ...DEFAULT_CREDITS }));
    }
  });
}

/** Returns true if the user has sufficient chat credits to make a paid request. */
export function hasSufficientChatCredits(credits: DebateCredits): boolean {
  return (credits.chatCredits ?? 0) > 0.001;
}

// ─── Weekly usage tracking ──────────────────────────────────────────────────

/** How many free text debates a signed-in user can run per week. */
export const WEEKLY_FREE_DEBATE_LIMIT = 10;
/** How many audio debates a signed-in user can run per week. */
export const WEEKLY_AUDIO_DEBATE_LIMIT = 3;

export interface WeeklyUsage {
  weekStart: number; // ms since epoch — Monday 00:00 UTC of the current week
  freeDebates: number; // how many free-tier debates used this week
  audioDebates: number; // how many audio debates used this week
}

/** Returns the Monday 00:00 UTC timestamp for the current week. */
function currentWeekStart(): number {
  const now = new Date();
  const day = now.getUTCDay(); // 0 = Sun, 1 = Mon, …
  const diff = day === 0 ? 6 : day - 1; // days since last Monday
  const monday = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - diff,
      0,
      0,
      0,
      0,
    ),
  );
  return monday.getTime();
}

function usageRef(uid: string) {
  return doc(db, "users", uid, "profile", "usage");
}

function emptyUsage(weekStart: number): WeeklyUsage {
  return { weekStart, freeDebates: 0, audioDebates: 0 };
}

/**
 * Subscribe to real-time usage updates. The doc is written only by the
 * startDebate Cloud Function; a doc from an earlier week reads as zero.
 */
export function onUsageSnapshot(
  uid: string,
  callback: (usage: WeeklyUsage) => void,
): Unsubscribe {
  return onSnapshot(usageRef(uid), (snap) => {
    const weekNow = currentWeekStart();
    const data = snap.exists() ? (snap.data() as WeeklyUsage) : null;
    callback(data && data.weekStart === weekNow ? data : emptyUsage(weekNow));
  });
}

// ─── Guest (localStorage) usage tracking ────────────────────────────────────

/** Weekly limit for guests — same as signed-in free users. */
export const GUEST_WEEKLY_FREE_DEBATE_LIMIT = 10;

const GUEST_USAGE_KEY = "guest_weekly_usage";

/** Read guest usage from localStorage, auto-resetting on a new week. */
export function getGuestUsage(): WeeklyUsage {
  const weekNow = currentWeekStart();
  try {
    const raw = localStorage.getItem(GUEST_USAGE_KEY);
    if (raw) {
      const data: WeeklyUsage = JSON.parse(raw);
      if (data.weekStart === weekNow) return data;
    }
  } catch {
    // corrupt data — fall through to default
  }
  const fresh: WeeklyUsage = {
    weekStart: weekNow,
    freeDebates: 0,
    audioDebates: 0,
  };
  localStorage.setItem(GUEST_USAGE_KEY, JSON.stringify(fresh));
  return fresh;
}

/** Increment a guest usage counter. Returns false if the weekly limit is reached. */
export function incrementGuestUsage(kind: "freeDebates"): boolean {
  const usage = getGuestUsage();
  if (usage[kind] >= GUEST_WEEKLY_FREE_DEBATE_LIMIT) return false;
  usage[kind] += 1;
  localStorage.setItem(GUEST_USAGE_KEY, JSON.stringify(usage));
  return true;
}
